import { Heading } from "@ariakit/react";
import { useMediaQuery } from "@indietabletop/appkit/use-media-query";
import { useCallback } from "react";
import { Link } from "wouter";
import { Army } from "../../domain/army";
import { CreateArmyDialog } from "../army-dialog";
import { atomic } from "../atomic.css";
import { ConfirmDialog } from "../confirm-dialog";
import { ContentView } from "../content-view";
import { useQuery } from "../database-provider";
import { DialogTrigger } from "../dialog-trigger";
import { KnownErrorView } from "../error-view";
import { Icon } from "../icon";
import { DialogListButton } from "../list-button";
import { LoadingView } from "../loading-view";
import { MinWidth } from "../media";
import { useRuleset } from "../ruleset-provider/context";
import { stalkers, wardens } from "../sample-armies-data";
import { VerticalDivider } from "../vertical-divider";
import * as css from "./style.css";

function ArmiesListingView(props: { armies: Army[] }) {
  const { armies } = props;

  return (
    <ContentView heading="Armies" backHref="~/" icon="back">
      {armies.length > 0 ? (
        <>
          <ul
            className={atomic({
              borderBlockStart: "solid",
            })}
          >
            {armies.map((a) => {
              return (
                <li
                  key={a.data.id}
                  className={atomic({
                    borderBlockEnd: { last: "none", default: "dashed" },
                  })}
                >
                  <Link
                    href={`/a/${a.data.id}`}
                    className={atomic({
                      display: "grid",
                      gridTemplateColumns: "fraction-auto",
                      alignItems: "center",
                      gap: 8,
                      paddingBlockStart: 20,
                      paddingBlockEnd: 18,
                      transition: "all",
                      backgroundColor: { hover: "background-secondary" },
                      paddingInlineStart: { hover: 16 },
                    })}
                  >
                    <div>
                      <div
                        className={atomic({
                          fontFamily: "barlow-condensed",
                          fontWeight: "semi-bold",
                          fontSize: 24,
                        })}
                      >
                        {a.name}
                      </div>
                      <div className={atomic({ lineHeight: 1.5 })}>
                        {a.summary}
                        {a.keyword && ` · ${a.keyword.name}`}
                      </div>
                    </div>
                    <Icon name="forward" />
                  </Link>
                </li>
              );
            })}
          </ul>

          <DialogTrigger>
            <CreateArmyDialog />
            <DialogListButton>Add army</DialogListButton>
          </DialogTrigger>
        </>
      ) : (
        <>
          <div className={atomic({ textAlign: "center", paddingBlock: 56 })}>
            <Heading
              className={atomic({
                fontFamily: "barlow-condensed",
                fontSize: 32,
                fontWeight: "semi-bold",
              })}
            >
              No armies yet
            </Heading>
            <p
              className={atomic({
                lineHeight: 1.5,
              })}
            >
              Add sample armies or start from scratch.
            </p>
          </div>

          <div
            className={atomic({
              maxInlineSize: "optimal-reading",
              marginInline: "auto",
            })}
          >
            <DialogTrigger>
              <AddSampleArmiesDialog />
              <DialogListButton>Add sample armies</DialogListButton>
            </DialogTrigger>

            <DialogTrigger>
              <CreateArmyDialog />
              <DialogListButton borderBlockStart="none">
                Add army
              </DialogListButton>
            </DialogTrigger>
          </div>
        </>
      )}
    </ContentView>
  );
}

function AddSampleArmiesDialog() {
  const ruleset = useRuleset();

  return (
    <ConfirmDialog
      heading="Add sample armies?"
      description="Two sample armies will be added. They are a great starting point for quickly trying out Eternøl."
      submitLabel="Add"
      onSubmit={async () => {
        await ruleset.db.cloneArmy(stalkers);
        await ruleset.db.cloneArmy(wardens);
      }}
    />
  );
}

export function ArmiesListingPage() {
  const isWide = useMediaQuery(MinWidth.WIDE);
  const ruleset = useRuleset();
  const armyDataOp = useQuery(
    useCallback((db) => db.getArmies(), []),
    "armies",
  );
  const armiesOp = armyDataOp.mapSuccess((armiesData) =>
    armiesData.map((armyData) => new Army(ruleset, armyData)),
  );

  if (armiesOp.isPending) {
    return <LoadingView />;
  }

  if (armiesOp.isFailure) {
    return <KnownErrorView code={armiesOp.failure.type} />;
  }

  const armies = armiesOp.value;

  if (!isWide) {
    return <ArmiesListingView armies={armies} />;
  }

  return (
    <div className={css.view}>
      <div className={atomic({ overflow: "auto" })}>
        <ArmiesListingView armies={armies} />
      </div>

      <div className={atomic({ overflow: "hidden" })}>
        <VerticalDivider />
      </div>

      <div className={css.illustrationWrap}>
        <div className={css.illustration}></div>
      </div>
    </div>
  );
}
